import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Table,
  Spinner,
  Alert,
  Modal,
  Form,
} from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaTags, FaArrowLeft, FaUndo, FaUsers } from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import LayoutRecordViewRoleBulkPage from './LayoutRecordViewRoleBulkPage';

/** Build API name from label: spaces → underscores, alphanumeric + underscore only, lowercase, no leading/trailing _. */
function labelToApiName(label) {
  if (label == null || typeof label !== 'string') return '';
  let s = label.trim().replace(/\s+/g, '_');
  s = s.replace(/[^A-Za-z0-9_]+/g, '_');
  s = s.replace(/_+/g, '_').replace(/^_|_$/g, '');
  if (/^[0-9]/.test(s)) {
    s = `_${s}`;
  }
  if (s.length > 100) {
    s = s.slice(0, 100).replace(/_+$/g, '');
  }
  return s.toLowerCase();
}

const emptyForm = () => ({
  name: '',
  view_label: '',
  description: '',
  is_active: true,
  is_deleted: false,
});

/** Prefill per-role layout picks from the default record view’s assignments (detail + edit). */
function buildRoleLayoutDraftFromContext({ roles, assignments, recordviews }) {
  const activeRoles = (roles || []).filter((r) => r.is_active !== false);
  const defRv = (recordviews || []).find(
    (rv) =>
      String(rv.name || '').toLowerCase() === 'default' || rv.is_standard === true
  );
  const defId = defRv ? String(defRv.id) : null;
  const draft = {};
  for (const r of activeRoles) {
    draft[String(r.id)] = { detailLayoutId: '', editLayoutId: '' };
  }
  if (defId) {
    for (const a of assignments || []) {
      if (String(a.recordview_id) !== defId) continue;
      const rid = String(a.role_id);
      if (!draft[rid]) continue;
      const lt = String(a.layout_type || '').toUpperCase();
      if (lt === 'DETAIL' && a.layout_id) draft[rid].detailLayoutId = String(a.layout_id);
      if (lt === 'EDIT' && a.layout_id) draft[rid].editLayoutId = String(a.layout_id);
    }
  }
  for (const rid of Object.keys(draft)) {
    const row = draft[rid];
    if (row.detailLayoutId && !row.editLayoutId) row.editLayoutId = row.detailLayoutId;
    if (row.editLayoutId && !row.detailLayoutId) row.detailLayoutId = row.editLayoutId;
  }
  return draft;
}

const RecordViewManager = ({ objectName, onBack }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  /** When false (new only), API name stays in sync with label until user edits the API field. */
  const [nameManualOverride, setNameManualOverride] = useState(false);
  /** Save / validation errors: shown inside the modal only (not above the list). */
  const [modalError, setModalError] = useState('');
  /** New record view: bulk-role-assign payload for roles / layouts / default RV assignments */
  const [createModalContext, setCreateModalContext] = useState(null);
  const [createModalLoading, setCreateModalLoading] = useState(false);
  /** roleId -> { detailLayoutId, editLayoutId } */
  const [roleLayoutDraft, setRoleLayoutDraft] = useState({});
  /** Remount "apply layout to all" select after each apply so it returns to the placeholder. */
  const [applyAllLayoutsKey, setApplyAllLayoutsKey] = useState(0);
  const [assignmentsModalOpen, setAssignmentsModalOpen] = useState(false);
  const [assignmentsHighlightRvId, setAssignmentsHighlightRvId] = useState(null);

  const load = useCallback(async () => {
    if (!objectName) return;
    setLoading(true);
    setError('');
    try {
      const api = new DataApi('recordview');
      const res = await api.get(`/object/${encodeURIComponent(objectName)}`);
      if (res?.data?.success) {
        setRows(res.data.data || []);
      } else {
        setRows([]);
        setError(res?.data?.message || 'Failed to load record views');
      }
    } catch (e) {
      setRows([]);
      setError(e.response?.data?.message || e.message || 'Failed to load record views');
    } finally {
      setLoading(false);
    }
  }, [objectName]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setNameManualOverride(false);
    setModalError('');
    setForm(emptyForm());
    setCreateModalContext(null);
    setRoleLayoutDraft({});
    setApplyAllLayoutsKey((k) => k + 1);
    setShowModal(true);
    setCreateModalLoading(true);
    (async () => {
      try {
        const api = new DataApi('objectconfig/layouts');
        const res = await api.get(`/bulk-role-assign/${encodeURIComponent(objectName)}`);
        if (res?.data?.success) {
          const d = res.data.data || {};
          setCreateModalContext({
            roles: d.roles || [],
            assignments: d.assignments || [],
            recordviews: d.recordviews || [],
            detailLayouts: d.layouts?.detail || [],
            editLayouts: d.layouts?.edit || [],
          });
          setRoleLayoutDraft(
            buildRoleLayoutDraftFromContext({
              roles: d.roles || [],
              assignments: d.assignments || [],
              recordviews: d.recordviews || [],
            })
          );
        } else {
          setModalError(res?.data?.message || 'Failed to load roles and layouts for this object.');
        }
      } catch (e) {
        setModalError(
          e.response?.data?.message || e.message || 'Failed to load roles and layouts for this object.'
        );
      } finally {
        setCreateModalLoading(false);
      }
    })();
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setNameManualOverride(true);
    setModalError('');
    setCreateModalContext(null);
    setRoleLayoutDraft({});
    setCreateModalLoading(false);
    const deleted = !!row.is_deleted;
    setForm({
      name: row.name || '',
      view_label: row.view_label || '',
      description: row.description || '',
      is_active: row.is_active !== false,
      is_deleted: deleted,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setModalError('');
    try {
      const api = new DataApi('recordview');
      const labelTrim = (form.view_label || '').trim();
      if (!labelTrim && !editingId) {
        setModalError('Label is required');
        setSaving(false);
        return;
      }
      if (editingId) {
        const payload = {
          view_label: labelTrim || null,
          description: form.description || null,
          is_active: form.is_active,
        };
        if (form.is_deleted) {
          const apiName = labelToApiName((form.name || '').trim());
          if (!apiName) {
            setModalError('API name is required for deleted record views.');
            setSaving(false);
            return;
          }
          payload.name = apiName;
        }
        const res = await api.update(payload, editingId);
        if (!res?.data?.success) {
          throw new Error(res?.data?.message || 'Update failed');
        }
        setSuccess('Record view updated');
      } else {
        let apiName = (form.name || '').trim();
        if (!apiName && labelTrim) {
          apiName = labelToApiName(labelTrim);
        } else {
          apiName = labelToApiName(apiName || labelTrim);
        }
        if (!apiName) {
          setModalError('Could not derive a valid API name from the label. Use letters or numbers.');
          setSaving(false);
          return;
        }
        if (!createModalContext) {
          setModalError('Roles and layouts are still loading or failed to load. Close and try New record view again.');
          setSaving(false);
          return;
        }
        const activeRoles = (createModalContext.roles || []).filter((r) => r.is_active !== false);
        const roleLayoutAssignments = activeRoles.map((r) => {
          const d = roleLayoutDraft[String(r.id)] || {};
          const detailId = d.detailLayoutId || d.editLayoutId || null;
          return {
            roleId: r.id,
            detailLayoutId: detailId,
            editLayoutId: detailId,
          };
        });
        const missingLayout = roleLayoutAssignments.some((x) => !x.detailLayoutId);
        if (missingLayout) {
          setModalError('Each role must have a detail layout (used for view and edit).');
          setSaving(false);
          return;
        }
        const payload = {
          object_name: objectName,
          name: apiName,
          view_label: labelTrim || null,
          description: form.description || null,
          is_active: form.is_active,
          roleLayoutAssignments,
        };
        const res = await api.create(payload);
        if (!res?.data?.success) {
          throw new Error(res?.data?.message || 'Create failed');
        }
        setSuccess('Record view created');
      }
      setShowModal(false);
      setNameManualOverride(false);
      setModalError('');
      await load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      const msg =
        e.response?.data?.message || e.message || 'Save failed';
      setModalError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete record view "${row.name}"?`)) return;
    setError('');
    try {
      const api = new DataApi('recordview');
      const res = await api.delete(row.id);
      if (!res?.data?.success) {
        throw new Error(res?.data?.message || 'Delete failed');
      }
      setSuccess('Record view deleted');
      await load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Delete failed');
    }
  };

  const handleRestore = async (row) => {
    if (!window.confirm(`Restore record view "${row.name}"? It will be active again.`)) return;
    setError('');
    try {
      const api = new DataApi('recordview');
      const res = await api.post(`/${row.id}/restore`, {});
      if (!res?.data?.success) {
        throw new Error(res?.data?.message || 'Restore failed');
      }
      setSuccess('Record view restored');
      await load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Restore failed');
    }
  };

  return (
    <div className="recordview-manager py-2">
      {onBack && (
        <Button variant="outline-secondary" size="sm" className="mb-3" onClick={onBack}>
          <FaArrowLeft className="me-1" />
          Back to Object
        </Button>
      )}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h4 className="mb-0">
          <FaTags className="me-2 text-secondary" />
          Record views — {objectName}
        </h4>
        <div className="d-flex flex-wrap gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => {
              setAssignmentsHighlightRvId(null);
              setAssignmentsModalOpen(true);
            }}
          >
            <FaUsers className="me-1" />
            View assignments
          </Button>
          <Button variant="primary" size="sm" onClick={openCreate}>
            <FaPlus className="me-1" />
            New record view
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : rows.length === 0 ? (
        <Alert variant="info">No record views yet. Create one to classify records for this object.</Alert>
      ) : (
        <Table responsive striped bordered hover size="sm" className="align-middle bg-white">
          <thead>
            <tr>
              <th>Label</th>
              <th>API name</th>
              <th>Status</th>
              <th>Active</th>
              <th style={{ width: '220px' }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className={r.is_deleted ? 'table-secondary' : ''}>
                <td>{r.view_label || '—'}</td>
                <td>
                  <code className="small">{r.name}</code>
                </td>
                <td>{r.is_deleted ? <span className="text-muted">Deleted</span> : 'Active'}</td>
                <td>{r.is_active !== false ? 'Yes' : 'No'}</td>
                <td className="text-nowrap">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="me-1"
                    title="Layout & role assignments for this record view"
                    onClick={() => {
                      setAssignmentsHighlightRvId(String(r.id));
                      setAssignmentsModalOpen(true);
                    }}
                  >
                    <FaUsers />
                  </Button>
                  <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEdit(r)}>
                    <FaEdit />
                  </Button>
                  {r.is_deleted ? (
                    <Button
                      variant="outline-success"
                      size="sm"
                      className="me-1"
                      title="Restore (undelete)"
                      onClick={() => handleRestore(r)}
                    >
                      <FaUndo />
                    </Button>
                  ) : (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      title="Delete"
                      onClick={() => handleDelete(r)}
                    >
                      <FaTrash />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal
        show={showModal}
        onHide={() => {
          if (!saving) {
            setShowModal(false);
            setNameManualOverride(false);
            setModalError('');
            setCreateModalContext(null);
            setRoleLayoutDraft({});
            setCreateModalLoading(false);
          }
        }}
        size={editingId ? 'lg' : 'xl'}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingId
              ? form.is_deleted
                ? 'Edit deleted record view'
                : 'Edit record view'
              : 'New record view'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError ? (
            <Alert variant="danger" dismissible onClose={() => setModalError('')} className="mb-3">
              {modalError}
            </Alert>
          ) : null}
          <Form.Group className="mb-3">
            <Form.Label>Label *</Form.Label>
            <Form.Control
              value={form.view_label}
              onChange={(e) => {
                const v = e.target.value;
                if (!editingId && !nameManualOverride) {
                  setForm({ ...form, view_label: v, name: labelToApiName(v) });
                } else {
                  setForm({ ...form, view_label: v });
                }
              }}
              placeholder="e.g. Customer record view"
              autoFocus
            />
            <Form.Text className="text-muted">
              Shown to users. For new record views, the API name below is filled automatically from this label.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>API name *</Form.Label>
            {editingId && !form.is_deleted ? (
              <Form.Control
                plaintext
                readOnly
                value={form.name}
                className="font-monospace border rounded px-3 py-2 bg-light"
              />
            ) : (
              <Form.Control
                value={form.name}
                onChange={(e) => {
                  if (!editingId) {
                    setNameManualOverride(true);
                  }
                  setForm({ ...form, name: e.target.value });
                }}
                onBlur={() => {
                  if ((form.name || '').trim()) {
                    setForm((prev) => ({ ...prev, name: labelToApiName(prev.name) }));
                  }
                }}
                placeholder="e.g. customer_record_view"
                className="font-monospace"
              />
            )}
            <Form.Text className="text-muted">
              {editingId && !form.is_deleted
                ? 'API name cannot be changed while this record view is active.'
                : editingId && form.is_deleted
                  ? 'Deleted record views only: rename the API name to free it for reuse (stored in lowercase).'
                  : 'Stored in lowercase. Spaces and special characters become underscores; only letters, numbers, and underscores. Unique in your org.'}
              {!editingId && !nameManualOverride ? ' Updates from the label until you edit this field.' : ''}
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </Form.Group>
          <div className="d-flex gap-4 mb-2">
            <Form.Check
              type="switch"
              id="rv-active"
              label="Active"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
          </div>
          {!editingId && (
            <div className="mt-4 border-top pt-3">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                <Form.Label className="mb-0 fw-semibold">Layouts per role (required)</Form.Label>
                {createModalContext && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    type="button"
                    disabled={createModalLoading || saving}
                    onClick={() =>
                      setRoleLayoutDraft(
                        buildRoleLayoutDraftFromContext({
                          roles: createModalContext.roles,
                          assignments: createModalContext.assignments,
                          recordviews: createModalContext.recordviews,
                        })
                      )
                    }
                  >
                    Reset to default record view
                  </Button>
                )}
              </div>
              <p className="text-muted small mb-2">
                Every active role needs a <strong>detail</strong> layout for this record view (same layout is used for view
                and edit). Values start as copies of the <strong>Default</strong> record view; adjust before saving.
              </p>
              {!createModalLoading && createModalContext ? (
                <div className="mb-3 p-2 rounded border bg-light">
                  <Form.Label className="small fw-semibold mb-1 d-block">Same layout for all roles</Form.Label>
                  <Form.Select
                    key={applyAllLayoutsKey}
                    size="sm"
                    className="w-100"
                    style={{ maxWidth: 420 }}
                    defaultValue=""
                    disabled={saving}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) return;
                      const activeRoles = (createModalContext.roles || []).filter((r) => r.is_active !== false);
                      setRoleLayoutDraft((prev) => {
                        const next = { ...prev };
                        for (const r of activeRoles) {
                          const rid = String(r.id);
                          const row = prev[rid] || { detailLayoutId: '', editLayoutId: '' };
                          next[rid] = { ...row, detailLayoutId: v, editLayoutId: v };
                        }
                        return next;
                      });
                      setApplyAllLayoutsKey((k) => k + 1);
                    }}
                  >
                    <option value="">Select a layout to apply to every role…</option>
                    {(createModalContext.detailLayouts || []).map((l) => (
                      <option key={l.layout_id} value={String(l.layout_id)}>
                        {l.layout_name}
                        {l.is_standard ? ' (standard)' : ''}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="d-block small text-muted mt-1 mb-0">
                    Per-role picks below stay editable after you use this.
                  </Form.Text>
                </div>
              ) : null}
              {createModalLoading ? (
                <div className="text-muted py-3">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading roles and layouts…
                </div>
              ) : createModalContext ? (
                <div className="recordview-create-layout-table-wrap border rounded" style={{ maxHeight: '280px', overflow: 'auto' }}>
                  <Table responsive size="sm" className="mb-0 align-middle">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th>Role</th>
                        <th>Detail layout (view &amp; edit)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(createModalContext.roles || [])
                        .filter((r) => r.is_active !== false)
                        .sort((a, b) =>
                          String(a.name || '').localeCompare(String(b.name || ''), undefined, {
                            sensitivity: 'base',
                          })
                        )
                        .map((r) => {
                          const rid = String(r.id);
                          const rowDraft = roleLayoutDraft[rid] || { detailLayoutId: '', editLayoutId: '' };
                          const pickVal = rowDraft.detailLayoutId || rowDraft.editLayoutId || '';
                          return (
                            <tr key={rid}>
                              <td className="small fw-medium">{r.name}</td>
                              <td>
                                <Form.Select
                                  size="sm"
                                  value={pickVal}
                                  disabled={saving}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setRoleLayoutDraft((prev) => ({
                                      ...prev,
                                      [rid]: { ...rowDraft, detailLayoutId: v, editLayoutId: v },
                                    }));
                                  }}
                                >
                                  <option value="">— None —</option>
                                  {(createModalContext.detailLayouts || []).map((l) => (
                                    <option key={l.layout_id} value={String(l.layout_id)}>
                                      {l.layout_name}
                                      {l.is_standard ? ' (standard)' : ''}
                                    </option>
                                  ))}
                                </Form.Select>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </Table>
                </div>
              ) : null}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowModal(false);
              setNameManualOverride(false);
              setModalError('');
              setCreateModalContext(null);
              setRoleLayoutDraft({});
              setApplyAllLayoutsKey((k) => k + 1);
              setCreateModalLoading(false);
            }}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || (!editingId && createModalLoading)}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={assignmentsModalOpen}
        onHide={() => {
          setAssignmentsModalOpen(false);
          setAssignmentsHighlightRvId(null);
        }}
        size="xl"
        fullscreen="lg-down"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Layout &amp; record view access</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {assignmentsModalOpen ? (
            <LayoutRecordViewRoleBulkPage
              embedded
              fixedObjectName={objectName}
              highlightRecordviewId={assignmentsHighlightRvId}
            />
          ) : null}
        </Modal.Body>
        <Modal.Footer className="py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <Button
            variant="link"
            size="sm"
            className="p-0"
            onClick={() => {
              setAssignmentsModalOpen(false);
              setAssignmentsHighlightRvId(null);
              const q = new URLSearchParams({ object: objectName });
              if (assignmentsHighlightRvId) q.set('recordview', assignmentsHighlightRvId);
              navigate(`/setup/layout-recordview-roles?${q.toString()}`);
            }}
          >
            Open in full page
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setAssignmentsModalOpen(false);
              setAssignmentsHighlightRvId(null);
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RecordViewManager;
